import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('loCompCapService', 'pricing-v2');

export interface LoCompCapData {
  /** `users.optimal_blue_current_percentage` — primary cap source. */
  optimalBluePct: number | null;
  /** `users.banked_compensation` — fallback cap when optimal_blue is not set. */
  bankedCompensationPct: number | null;
  /**
   * True when the user carries a role in EXEMPT_ROLES ('partner' or 'shareholder',
   * lowercased match against users.roles). Partners/shareholders are exempt from the
   * comp-plan cap — but RESPA dual-comp still wins if present. See useBpcCap for the
   * precedence order.
   */
  isExempt: boolean;
}

/**
 * Roles exempted from the LO comp-plan cap. Lowercased match. `shareholder` is not
 * currently a validated role value in migration 114 — including it here is harmless
 * (no user row carries it today, so the match never fires), but documents product
 * intent if a future migration widens the role set.
 */
export const EXEMPT_ROLES = ['partner', 'shareholder'] as const;
type ExemptRole = typeof EXEMPT_ROLES[number];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuidLike(value: string): boolean {
  return UUID_RE.test(value.trim());
}

/**
 * Exported for direct testing. Derives the comp-plan-cap exemption flag from
 * a raw users.roles value. Case-insensitive; matches any string element in
 * `roles` (or derivative) against EXEMPT_ROLES. Returns false for null,
 * undefined, non-arrays, empty arrays, or arrays without an exempt role.
 *
 * Keep in sync with the Edge Function's local copy in
 * supabase/functions/enqueue-encompass-write/index.ts (Fix 4).
 */
export function resolveIsExempt(roles: unknown): boolean {
  if (!Array.isArray(roles)) {
    return false;
  }
  return roles.some((r) => EXEMPT_ROLES.includes(String(r ?? '').toLowerCase() as ExemptRole));
}

/**
 * Fetch the assigned LO's comp-plan cap sources for BPC validation.
 *
 * Phase 2 uses this both client-side (useBpcCap hook at aside input gate) and
 * server-side (enqueue-encompass-write re-validation before mutation). The
 * server path reads the assigned LO from encompass_loans.loan_officer_id —
 * NOT the acting user — so admins/processors saving a group are still gated
 * by the LO's plan.
 *
 * Returns `{ null, null }` when the user row is missing or neither column is
 * set. Callers decide how to handle both-null (the hook treats it as
 * `allowEdit: false, reason: 'No comp plan configured'`).
 */
export async function getLoCompCapData(loanOfficerId: string): Promise<LoCompCapData> {
  // `encompass_loans.loan_officer_id` is typically an Encompass identifier, not
  // a Lendwise UUID (see src/services/bankerReconService.ts:752,
  // src/services/loanPricingService.ts:756). Try encompass_user_id first, fall
  // back to users.id so any legacy/test paths that pass a UUID directly still work.
  const { data: byEncompass, error: encErr } = await supabase
    .from('users')
    .select('optimal_blue_current_percentage, banked_compensation, roles')
    .eq('encompass_user_id', loanOfficerId)
    .maybeSingle();
  if (encErr) {
    log.error('Failed lookup by encompass_user_id', { loanOfficerId }, encErr);
  }
  if (byEncompass) {
    return {
      optimalBluePct: toFinitePositiveOrNull(byEncompass.optimal_blue_current_percentage),
      bankedCompensationPct: toFinitePositiveOrNull(byEncompass.banked_compensation),
      isExempt: resolveIsExempt(byEncompass.roles)
    };
  }

  if (!isUuidLike(loanOfficerId)) {
    log.debug('No user row for non-UUID loanOfficerId by encompass_user_id', { loanOfficerId });
    return { optimalBluePct: null, bankedCompensationPct: null, isExempt: false };
  }

  const { data: byId, error: idErr } = await supabase
    .from('users')
    .select('optimal_blue_current_percentage, banked_compensation, roles')
    .eq('id', loanOfficerId)
    .maybeSingle();
  if (idErr) {
    log.error('Failed lookup by users.id', { loanOfficerId }, idErr);
    return { optimalBluePct: null, bankedCompensationPct: null, isExempt: false };
  }
  if (!byId) {
    log.debug('No user row for loanOfficerId (neither encompass_user_id nor id)', { loanOfficerId });
    return { optimalBluePct: null, bankedCompensationPct: null, isExempt: false };
  }
  return {
    optimalBluePct: toFinitePositiveOrNull(byId.optimal_blue_current_percentage),
    bankedCompensationPct: toFinitePositiveOrNull(byId.banked_compensation),
    isExempt: resolveIsExempt(byId.roles)
  };
}

function toFinitePositiveOrNull(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n;
}
